import React, {type FC, useCallback, useMemo} from 'react';
import s from './ProductQuickAddContent.scss';
import {useGlobals, withGlobals} from '../../../../../globalPropsContext';
import {IProduct} from '../../../../../types/galleryTypes';
import {IProductOption, ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src';
import {ProductQuickAddColorOption} from './ProductQuickAddColorOption/ProductQuickAddColorOption';
import {ProductQuickAddTextualOption} from './ProductQuickAddTextualOption/ProductQuickAddTextualOption';
import {ProductQuickAddAddToCartButton} from './ProductQuickAddAddToCartButton/ProductQuickAddAddToCartButton';
import {canAddToCart} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import {useAutoAddToCart} from './hooks/useAutoAddToCart';

export enum ProductQuickAddContentDataHook {
  Root = 'ProductQuickAddContentDataHook.Root',
  QuickAddAddToCartButton = 'ProductQuickAddContentDataHook.QuickAddAddToCartButton',
}

export interface IProductQuickAddContentProps {
  product: IProduct;
  quantity: number;
}

const ProductQuickAddContentComp: FC<IProductQuickAddContentProps> = ({product, quantity}) => {
  const {styles, stylesParams, productsVariantInfoMap} = useGlobals();

  const canAddProductToCart = useMemo(() => {
    const variantSelectionIds = productsVariantInfoMap?.[product.id]?.variantSelectionIds;
    return canAddToCart({product, variantSelectionIds});
  }, [product, productsVariantInfoMap]);

  useAutoAddToCart({product, quantity, canAddProductToCart});

  const renderOption = useCallback(
    (option: IProductOption) => {
      const commonProps = {
        key: option.id,
        productId: product.id,
        option,
      };

      if (option.optionType === ProductOptionType.COLOR) {
        return <ProductQuickAddColorOption {...commonProps} />;
      }

      return <ProductQuickAddTextualOption {...commonProps} />;
    },
    [product.id]
  );

  const isAddToCartButtonVisible = styles.get(stylesParams.gallery_quickAddShowAddToCartButton);

  return (
    <div className={s.quickAddContent} data-hook={ProductQuickAddContentDataHook.Root}>
      <div className={s.quickAddOptions}>{product.options.map(renderOption)}</div>
      {isAddToCartButtonVisible && (
        <ProductQuickAddAddToCartButton
          product={product}
          quantity={quantity}
          canAddProductToCart={canAddProductToCart}
        />
      )}
    </div>
  );
};

export const ProductQuickAddContent = withGlobals(ProductQuickAddContentComp);
